import React from "react";
import {
  Box, Typography, Checkbox, FormControlLabel, Stack,
  Chip, alpha, useTheme,
} from "@mui/material";
import WebIcon from "@mui/icons-material/WebOutlined";
import { ALL_PAGES, PAGE_GROUPS } from "utils/pagePermissions";

/**
 * Checkbox grid for selecting which frontend pages a role can access.
 * pages: string[]   — current selection
 * onChange: (pages: string[]) => void
 */
const PagePermissionEditor = ({ pages = [], onChange }) => {
  const theme = useTheme();

  /**
   * Checking a page auto-grants all its tabs (tab visibility requires the exact
   * tab slug — there's no "parent implies all tabs" fallback, see
   * utils/pagePermissions.getVisibleTabs). Unchecking a page revokes it and
   * all its tab slugs together.
   */
  const togglePage = (page) => {
    const tabSlugs = (page.tabs || []).map((t) => t.slug);
    const isOn = pages.includes(page.slug);
    const next = isOn
      ? pages.filter((s) => s !== page.slug && !tabSlugs.includes(s))
      : [...new Set([...pages, page.slug, ...tabSlugs])];
    onChange(next);
  };

  const toggleTab = (tabSlug) => {
    const next = pages.includes(tabSlug)
      ? pages.filter((s) => s !== tabSlug)
      : [...pages, tabSlug];
    onChange(next);
  };

  const toggleGroup = (group) => {
    const groupPages = ALL_PAGES.filter((p) => p.group === group);
    const groupSlugs = groupPages.flatMap((p) => [p.slug, ...(p.tabs || []).map((t) => t.slug)]);
    const allSelected = groupPages.every((p) => pages.includes(p.slug));
    const next = allSelected
      ? pages.filter((s) => !groupSlugs.includes(s))
      : [...new Set([...pages, ...groupSlugs])];
    onChange(next);
  };

  return (
    <Box>
      {pages.length === 0 ? null : (
        <Chip
          label={`${pages.length} page${pages.length !== 1 ? "s" : ""} allowed`}
          size="small" color="secondary" variant="outlined"
          sx={{ mb: 1.5, height: 20, fontSize: "0.7rem" }}
        />
      )}

      {PAGE_GROUPS.map((group) => {
        const groupPages = ALL_PAGES.filter((p) => p.group === group);
        const allSelected = groupPages.every((p) => pages.includes(p.slug));
        const someSelected = groupPages.some((p) => pages.includes(p.slug));

        return (
          <Box key={group} mb={1.5}>
            <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
              <WebIcon sx={{ fontSize: 14, color: "text.secondary" }} />
              <Typography variant="caption" fontWeight={700} color="text.secondary"
                sx={{ textTransform: "uppercase", letterSpacing: "0.07em" }}>
                {group}
              </Typography>
              <FormControlLabel
                label={<Typography variant="caption" color="text.secondary">All</Typography>}
                control={
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected && !allSelected}
                    onChange={() => toggleGroup(group)}
                    sx={{ p: 0.25, mr: 0.25 }}
                    color="secondary"
                  />
                }
                sx={{ ml: 0.5, mr: 0 }}
              />
            </Stack>
            <Box
              sx={{
                display: "flex", flexDirection: "column", flexWrap: "wrap", gap: 0.75, pl: 2.5,
                pb: 1, borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              {groupPages.map((page) => {
                const { slug, label, tabs } = page;
                const checked = pages.includes(slug);
                return (
                  <Box key={slug}>
                    <Box
                      onClick={() => togglePage(page)}
                      sx={{
                        display: "inline-flex", alignItems: "center", gap: 0.5,
                        px: 1, py: 0.25, borderRadius: 1, cursor: "pointer",
                        border: `1px solid`,
                        borderColor: checked ? theme.palette.secondary.main : theme.palette.divider,
                        backgroundColor: checked
                          ? alpha(theme.palette.secondary.main, 0.1)
                          : "transparent",
                        "&:hover": {
                          backgroundColor: alpha(theme.palette.secondary.main, 0.07),
                        },
                      }}
                    >
                      <Checkbox
                        size="small" checked={checked}
                        onChange={() => togglePage(page)}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ p: 0 }} color="secondary"
                      />
                      <Typography variant="caption" fontWeight={checked ? 600 : 400}>
                        {label}
                      </Typography>
                    </Box>

                    {tabs && (
                      <Box
                        sx={{
                          display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5, pl: 2.5,
                          opacity: checked ? 1 : 0.45,
                          pointerEvents: checked ? "auto" : "none",
                        }}
                      >
                        {tabs.map((tab) => {
                          const tabChecked = pages.includes(tab.slug);
                          return (
                            <Box
                              key={tab.slug}
                              onClick={() => toggleTab(tab.slug)}
                              sx={{
                                display: "flex", alignItems: "center", gap: 0.5,
                                px: 1, py: 0.125, borderRadius: 1, cursor: "pointer",
                                border: `1px dashed`,
                                borderColor: tabChecked ? theme.palette.secondary.main : theme.palette.divider,
                                backgroundColor: tabChecked
                                  ? alpha(theme.palette.secondary.main, 0.08)
                                  : "transparent",
                                "&:hover": {
                                  backgroundColor: alpha(theme.palette.secondary.main, 0.05),
                                },
                              }}
                            >
                              <Checkbox
                                size="small" checked={tabChecked}
                                onChange={() => toggleTab(tab.slug)}
                                onClick={(e) => e.stopPropagation()}
                                sx={{ p: 0 }} color="secondary"
                              />
                              <Typography variant="caption" sx={{ fontSize: "0.68rem" }}>
                                {tab.label}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default PagePermissionEditor;
